import { useEffect, useRef } from "preact/hooks";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { SkeletonLoader } from "../SkeletonLoader";
import { ErrorMessage } from "../ErrorMessage";
import {
  getChartColors,
  colorWithAlpha,
  isoToUnixSeconds,
  createTooltip,
  makeTimeFormatter,
  useChartTheme,
} from "./chartUtils";

type ResourceChartProps = {
  data: Array<{
    timestamp: string;
    cpu_load: number;
    memory_percent: number;
  }>;
  loading?: boolean;
  error?: string;
};

function formatPercentAxis(_u: uPlot, splits: number[]): string[] {
  return splits.map((v) => `${v}%`);
}

export function ResourceChart({ data, loading, error }: ResourceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const theme = useChartTheme();

  useEffect(() => {
    if (loading || error || data.length === 0) return;
    if (!containerRef.current) return;

    const colors = getChartColors();
    const tooltip = createTooltip();
    tooltipRef.current = tooltip;
    containerRef.current.appendChild(tooltip);

    const timestamps = data.map((d) => isoToUnixSeconds(d.timestamp));
    const cpu = data.map((d) => d.cpu_load);
    const memory = data.map((d) => d.memory_percent);

    const width = containerRef.current.clientWidth;
    const height = 300;

    const opts: uPlot.Options = {
      width,
      height,
      cursor: { drag: { x: false, y: false } },
      legend: { show: true },
      scales: {
        y: {
          range: [0, 100],
        },
      },
      axes: [
        {
          stroke: colors.text,
          grid: { stroke: colors.grid },
          ticks: { stroke: colors.grid },
          values: makeTimeFormatter(timestamps),
        },
        {
          stroke: colors.text,
          grid: { stroke: colors.grid },
          ticks: { stroke: colors.grid },
          values: formatPercentAxis,
          size: 50,
        },
      ],
      series: [
        {},
        {
          label: "CPU Load",
          stroke: colors.blue,
          fill: colorWithAlpha(colors.blue, 0.15),
          width: 2,
        },
        {
          label: "Memory",
          stroke: colors.orange,
          fill: colorWithAlpha(colors.orange, 0.15),
          width: 2,
        },
      ],
      hooks: {
        setCursor: [
          (u) => {
            const idx = u.cursor.idx;
            if (idx == null || idx < 0) {
              tooltip.style.display = "none";
              return;
            }
            const t = u.data[0][idx];
            const cpuVal = u.data[1][idx];
            const memVal = u.data[2][idx];
            if (t == null || cpuVal == null || memVal == null) {
              tooltip.style.display = "none";
              return;
            }
            tooltip.innerHTML = `
              <div><strong>${new Date(t * 1000).toLocaleString("id-ID")}</strong></div>
              <div style="color:${colors.blue}">CPU: ${cpuVal}%</div>
              <div style="color:${colors.orange}">RAM: ${memVal}%</div>
            `;
            tooltip.style.display = "block";
            tooltip.style.left = `${(u.cursor.left ?? 0) + 12}px`;
            tooltip.style.top = `${(u.cursor.top ?? 0) + 12}px`;
          },
        ],
      },
    };

    const chart = new uPlot(opts, [timestamps, cpu, memory], containerRef.current);
    chartRef.current = chart;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        chart.setSize({ width: entry.contentRect.width, height });
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.destroy();
      tooltip.remove();
      chartRef.current = null;
      tooltipRef.current = null;
    };
  }, [data, theme, loading, error]);

  if (loading) return <SkeletonLoader type="chart" />;
  if (error) return <ErrorMessage message={error} />;
  if (data.length === 0) return <div class="chart-empty">Belum ada data</div>;

  return <div ref={containerRef} class="chart-container" />;
}
